import { useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  Home,
  UtensilsCrossed,
  Users,
  Camera,
  Gift,
  HelpCircle,
  Info,
  Store,
  LayoutGrid,
  Tag,
} from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import { cn } from "@/lib/utils";

const eatzyLogo = "/eatzy-disc-1024.png";

interface AppSidebarProps {
  onCreateClick?: () => void;
}

const primaryNavItems = [
  { id: "feed", label: "Home", icon: Home, path: "/" },
  { id: "restaurants", label: "Restaurants", icon: UtensilsCrossed, path: "/restaurants" },
  { id: "deals", label: "Deals", icon: Tag, path: "/deals" },
  { id: "friends", label: "Friends", icon: Users, path: "/friends" },
  { id: "myeatzy", label: "My Eatzy", icon: LayoutGrid, path: "/my-eatzy" },
];

const secondaryNavItems = [
  { id: "rewards", label: "Rewards", icon: Gift, path: "/rewards" },
  { id: "gift-certificates", label: "Gift Certificates", icon: Gift, path: "/gift-certificates" },
  { id: "faq", label: "FAQ", icon: HelpCircle, path: "/faq" },
  { id: "about", label: "About Us", icon: Info, path: "/about" },
  { id: "owner", label: "Add Your Restaurant", icon: Store, path: "/owner" },
];

export function AppSidebar({ onCreateClick }: AppSidebarProps) {
  const [location, setLocation] = useLocation();
  const profileContext = useUserProfileOptional();
  const profile = profileContext?.profile;

  const isActive = (path: string) => {
    if (path === "/") return location === "/";
    if (path === "/restaurants") return location.startsWith("/restaurants") || location.startsWith("/restaurant/");
    return location.startsWith(path);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <button
          onClick={() => setLocation("/")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src={eatzyLogo} alt="Eatzy Kudoz" className="w-8 h-8 flex-shrink-0" />
          <div className="overflow-hidden group-data-[collapsible=icon]:hidden">
            <h1 className="font-bold text-base leading-none">Eatzy</h1>
            <p className="text-xs text-muted-foreground leading-none">Kudoz</p>
          </div>
        </button>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {primaryNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={item.label}
                    >
                      <button
                        onClick={() => setLocation(item.path)}
                        className="w-full"
                      >
                        <Icon className={cn("w-5 h-5", active && "fill-current")} />
                        <span>{item.label}</span>
                      </button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={onCreateClick}
                  tooltip="Write a Review"
                >
                  <Camera className="w-5 h-5" />
                  <span>Write a Review</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
            More
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={item.label}
                    >
                      <button
                        onClick={() => setLocation(item.path)}
                        className="w-full"
                      >
                        <Icon className={cn("w-5 h-5", active && "fill-current")} />
                        <span>{item.label}</span>
                      </button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {profile && (
        <SidebarFooter className="p-3">
          <button
            onClick={() => setLocation("/profile")}
            className="flex items-center gap-2 w-full rounded-md p-2 hover-elevate active-elevate-2 transition-colors text-left"
          >
            <span className="relative shrink-0">
              <UserAvatar
                src={profile.profilePicture}
                name={profile.displayName || profile.name || "User"}
                size="sm"
              />
            </span>
            <div className="flex-1 min-w-0 overflow-hidden group-data-[collapsible=icon]:hidden">
              <p className="text-sm font-medium leading-none truncate">
                {profile.displayName || profile.name || "User"}
              </p>
              <p className="text-xs text-muted-foreground leading-none mt-0.5 truncate">
                View profile
              </p>
            </div>
          </button>
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
